import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from "react";

import { last, pipe, sortBy } from "remeda";
import { useLocalStorage } from "usehooks-ts";

import { ShowroomSeason } from "@models/types/enums";
import { GetShowroomsEndpoint } from "@services/api/showroom/get-showrooms";
import { useOrganizationAppContext } from "@services/application/useApplicationContext";
import getLastOpeningDate from "@shared/showroom/helpers";

import { currentWholesalesSeasonYear } from "../crm/helpers/find-season";

type ContextValues = {
  season?: ShowroomSeason;
  year?: number;
  compareToSeason?: ShowroomSeason;
  compareToYear?: number;
};

type ContextActions = {
  setCurrentSeasonYear: (season: ShowroomSeason, year: number) => void;
  setCompareToSeasonYear: (season: ShowroomSeason, year: number) => void;
};

type SeasonCompareContextType = ContextValues & ContextActions;

export const SeasonCompareContext = createContext<SeasonCompareContextType>({
  season: currentWholesalesSeasonYear().season,
  year: currentWholesalesSeasonYear().year,
  compareToSeason: undefined,
  compareToYear: undefined,
  setCurrentSeasonYear: () => {},
  setCompareToSeasonYear: () => {},
});

export function useSeasonCompareContext() {
  return useContext(SeasonCompareContext);
}

export function SeasonCompareContextProvider({
  name,
  children,
}: PropsWithChildren<{ name: string }>) {
  const { organization } = useOrganizationAppContext();
  const organizationId = organization.id;
  const { data: showrooms } = GetShowroomsEndpoint.useSuspenseHook({
    organizationId,
  });

  const lastShowroom = pipe(
    showrooms,
    sortBy((s) => getLastOpeningDate(s)),
    last(),
  );

  const [seasonsToCompare, setSeasonsToCompare] =
    useLocalStorage<ContextValues>(name, {
      season: lastShowroom?.season,
      year: lastShowroom?.year,
      compareToSeason: undefined,
      compareToYear: undefined,
    });

  const memoizedContext = useMemo(
    () => ({
      ...seasonsToCompare,
      setCurrentSeasonYear: (season: ShowroomSeason, year: number) => {
        setSeasonsToCompare({
          ...seasonsToCompare,
          season,
          year,
        });
      },
      setCompareToSeasonYear: (season: ShowroomSeason, year: number) => {
        setSeasonsToCompare({
          ...seasonsToCompare,
          compareToSeason: season,
          compareToYear: year,
        });
      },
    }),
    [seasonsToCompare, setSeasonsToCompare],
  );

  return (
    <SeasonCompareContext.Provider value={memoizedContext}>
      {children}
    </SeasonCompareContext.Provider>
  );
}
